<template>
  <div class="sqls-integracao">
    <Loading :isActive="loading" />

    <!-- Banner de ambiente -->
    <div
      class="ambiente-banner"
      :class="isProducao ? 'ambiente-banner--prod' : 'ambiente-banner--impl'"
    >
      <fa-icon :icon="isProducao ? 'exclamation-triangle' : 'cogs'" />
      <span v-if="isProducao">
        Ambiente de <strong>PRODUÇÃO</strong> — alterações de SQL são controladas e auditáveis.
      </span>
      <span v-else>
        Ambiente de <strong>IMPLANTAÇÃO</strong> — alterações de SQL são liberadas.
      </span>
    </div>

    <!-- Nota explicativa recolhível -->
    <div class="nota" :class="{ 'nota--aberta': notaAberta }">
      <button type="button" class="nota__head" @click="notaAberta = !notaAberta">
        <fa-icon icon="chevron-down" class="nota__chevron" />
        <span>Sobre os SQLs de integração</span>
      </button>
      <div v-show="notaAberta" class="nota__corpo">
        SQLs de integração sobrescrevem as consultas padrão de extração de dados do ERP para
        dentro das plataformas, permitindo regras específicas da operação. O nome da consulta
        segue o mesmo nome da view no banco de dados, para facilitar a localização.
      </div>
    </div>

    <!-- Barra de ferramentas -->
    <div class="toolbar">
      <span class="toolbar__search">
        <fa-icon icon="search" class="toolbar__search-icon" />
        <input
          type="text"
          v-model="termo"
          placeholder="Buscar por nome ou descrição..."
        >
      </span>
      <button class="btn-novo" @click="abrirEditor({})">
        <fa-icon icon="plus" /> Novo registro
      </button>
    </div>

    <!-- Filtro de status (chips) -->
    <StatusFilterBar v-model="filtroStatus" :contagens="contagens" :chips="chipsStatus" />

    <!-- Tabela -->
    <div class="table-responsive">
      <table class="table table-hover tabela-views">
        <thead>
        <tr>
          <th>View</th>
          <th>Descrição</th>
          <th>Ativo</th>
          <th>Carga vinculada</th>
          <th class="text-right">Ações</th>
        </tr>
        </thead>
        <tbody>
        <ViewRow
          v-for="model in listaViews"
          :key="model.view_name"
          :model="model"
          @visualizar="simulateQuery"
          @editar="abrirEditor"
          @excluir="excluir"
          @restaurar="({ model: m, sql }) => onRestaurarSql(m, sql)"
        />
        <tr v-if="listaViews.length === 0">
          <td colspan="5" class="vazio">Nenhuma view encontrada para este filtro.</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="rodape-contagem">
      {{ listaViews.length }} de {{ views.length }} views
    </div>

    <EditorViewSql
      ref="editor"
      :views-sincronizaveis="viewsSincronizaveis"
      @executar="executarSql"
      @salvar="onSalvar"
    />
    <ModalSimulateQuery :sql="currentSql" />
    <ModalMotivoAlteracao ref="motivoModal" />
  </div>
</template>

<script>
  import ModalSimulateQuery from "@/views/SqlErpIntegracao/sqls/ModalSimulateQuery";
  import ModalMotivoAlteracao from "@/views/SqlErpIntegracao/sqls/ModalMotivoAlteracao";
  import EditorViewSql from "@/views/SqlErpIntegracao/sqls/EditorViewSql";
  import StatusFilterBar from "@/components/StatusFilterBar";
  import ViewRow from "@/views/SqlErpIntegracao/sqls/ViewRow";
  import { cargaDe, matchFilterView } from "@/views/SqlErpIntegracao/sqls/sqlCargaSaude";
  import { validarComentariosSql } from "@/helpers/sql/sqlValidacao";
  import { ehPhp } from "@/helpers/sql/sqlErpHelpers";
  import { get } from "@/helpers/apiRequest";
  import { confirmUpdate, confirmStore, confirmDelete } from "@/helpers/crud";
  import { arrayFilter, normalizeString } from "@/helpers/utils";
  import config from "@/config";

  export default {
    name: "SQLsErpIntegracao",
    components: {
      ModalSimulateQuery,
      ModalMotivoAlteracao,
      EditorViewSql,
      StatusFilterBar,
      ViewRow,
    },
    data() {
      return {
        url: '/admin/sqls-erp',
        ambiente: config.ambiente,
        exigeMotivo: config.exigeMotivo,
        loading: false,
        currentSql: null,
        viewsSincronizaveis: [],
        views: [],
        termo: '',
        filtroStatus: 'todas',
        notaAberta: false,
        // chips do StatusFilterBar. Aqui "Ativas/Inativas" referem-se ao campo
        // `ativo` da própria view; "Carga quebrada" usa a saúde da carga vinculada.
        chipsStatus: [
          { chave: 'todas', label: 'Todas', cor: '#3a3e81' },
          { chave: 'ativas', label: 'Ativas', cor: '#3a3e81' },
          { chave: 'inativas', label: 'Inativas', cor: '#5a6072' },
          { chave: 'quebrada', label: 'Carga quebrada', dot: true, cor: '#c0392b', borda: '#f3cdc8' },
        ],
      }
    },
    computed: {
      isProducao() {
        return this.ambiente === 'producao';
      },
      listaViews() {
        const porTexto = arrayFilter(this.views, this.termo, ['view_name', 'descricao']);
        const porStatus = porTexto.filter((m) => matchFilterView(m, this.filtroStatus, m._carga));
        return this.ordenarPorRelevancia(porStatus);
      },
      contagens() {
        const chaves = ['todas', 'ativas', 'inativas', 'quebrada'];
        const resultado = {};
        chaves.forEach((chave) => {
          resultado[chave] = this.views.filter((m) => matchFilterView(m, chave, m._carga)).length;
        });
        return resultado;
      },
    },
    methods: {
      loadData() {
        this.loading = true;
        get(this.url)
          .then((json) => {
            const lista = (json && json.data) || json || [];
            // Normaliza a carga vinculada UMA vez por item; contagens, filtros e
            // linhas usam `_carga` pronto em vez de realocar objetos por computed.
            this.views = lista.map((v) => ({ ...v, _carga: cargaDe(v) }));
          })
          .catch(() => { this.views = []; })
          .finally(() => { this.loading = false; });
      },
      /**
       * Ordena por relevância ao termo: match no nome da view vence match só na
       * descrição (ex.: buscar "produto" traz V_BDTI_PRODUTO primeiro, não uma
       * view cuja descrição menciona "produto"). Sem termo, mantém a ordem atual.
       * Escala do score (menor = mais relevante):
       *   0 nome == termo | 1 nome começa com termo | 2 nome contém termo |
       *   3 match só na descrição. Empate: nome mais curto e, então, alfabético.
       */
      ordenarPorRelevancia(lista) {
        const termo = normalizeString(String(this.termo || '').toLowerCase()).trim();
        if (!termo) return lista;

        const score = (item) => {
          const nome = normalizeString(String(item.view_name || '').toLowerCase());
          if (nome === termo) return 0;
          if (nome.startsWith(termo)) return 1;
          if (nome.indexOf(termo) >= 0) return 2;
          return 3;
        };

        // Copia antes de ordenar: não mutar o array vindo do filter/computed.
        return [...lista].sort((a, b) => {
          const sa = score(a);
          const sb = score(b);
          if (sa !== sb) return sa - sb;
          const na = String(a.view_name || '');
          const nb = String(b.view_name || '');
          if (na.length !== nb.length) return na.length - nb.length;
          return na.localeCompare(nb);
        });
      },
      abrirEditor(item) {
        this.$refs.editor.abrir(item || {});
      },
      simulateQuery(model) {
        this.executarSql(model.sql);
      },
      executarSql(sql) {
        // Zera antes de setar: reabrir a mesma view não dispararia o watch de
        // `sql` do modal (valor igual) e o resultado antigo ficaria congelado.
        this.currentSql = null;
        this.$nextTick(() => {
          this.currentSql = sql;
          this.$modal.show('simulate-query');
        });
      },
      /**
       * Exclusão com o mesmo rigor da edição: motivo obrigatório quando o
       * ambiente exige (enviado no corpo do DELETE) e confirmação extra em
       * produção — além da confirmação padrão do confirmDelete.
       */
      async excluir(model) {
        const extra = await this.confirmarMotivoOuProducao(model.view_name, {
          titulo: 'Excluir em produção?',
          texto: 'Você está excluindo uma consulta em produção.',
          confirmar: 'Confirmar e excluir',
        });
        if (extra === false) {
          return;
        }
        const body = (extra && typeof extra === 'object') ? extra : {};
        confirmDelete(
          `${this.url}/${encodeURIComponent(model.view_name)}`,
          () => this.loadData(),
          body
        );
      },
      /**
       * Restaurar versão: abre o editor com o SQL escolhido carregado (revisão + novo
       * save, que passa por motivo/auditoria).
       */
      onRestaurarSql(model, sql) {
        this.abrirEditor({ ...model, sql });
      },
      /**
       * Salvar disparado pelo EditorViewSql: bloqueia comentários que quebram a
       * carga, exige motivo na edição (quando o ambiente pede) ou confirma
       * produção, e então persiste reaproveitando os helpers de CRUD existentes.
       * O SQL não é executado no salvar: em views pesadas a execução levava
       * minutos sem retorno na tela. Para testar a consulta, use "Executar SQL".
       */
      async onSalvar(payload, action) {
        const extra = await this.beforeSave(payload, action);
        if (extra === false) {
          return;
        }
        const corpo = (extra && typeof extra === 'object') ? { ...payload, ...extra } : payload;

        const onSuccess = () => {
          this.loadData();
          this.$refs.editor.fecharForcado();
        };

        if (action === 'update') {
          confirmUpdate(`${this.url}/${encodeURIComponent(payload.view_name)}`, corpo, onSuccess);
        } else {
          confirmStore(this.url, corpo, onSuccess);
        }
      },
      /**
       * Bloqueio de comentários + motivo (só na edição) + confirmação de produção.
       * Retorna { motivo } para mesclar no payload, true para seguir, ou false para abortar.
       */
      async beforeSave(row, action) {
        // Base única: a coluna `sql_erp.tipo`. Só "sql" é SQL; o resto (ex.: "PHP")
        // pula o bloqueio dos comentários.
        const ehSql = !ehPhp(row.tipo);
        if (ehSql && row.sql) {
          // Bloqueio rígido: a montagem da carga concatena trechos após a query,
          // então o SQL não pode terminar em comentário -- ativo nem conter um
          // bloco /* sem fechamento (ambos comentariam esse complemento).
          const valida = validarComentariosSql(row.sql);
          if (!valida.ok) {
            const mensagens = {
              'comentario-final':
                'A consulta termina em um comentário de linha ' +
                '(<code>--</code>). Isso impede que a carga seja montada ' +
                'corretamente.<br><br>' +
                'Remova o comentário do final ou converta-o em bloco ' +
                '(<code>/* ... */</code>) antes de salvar.',
              'bloco-aberto':
                'A consulta contém um comentário de bloco aberto ' +
                '(<code>/*</code> sem o <code>*/</code> correspondente). ' +
                'Isso impede que a carga seja montada corretamente.<br><br>' +
                'Feche o comentário de bloco antes de salvar.',
            };
            await this.$swal({
              title: 'Não é possível salvar',
              html: mensagens[valida.motivo],
              icon: 'error',
              confirmButtonText: 'Entendi',
            });
            return false;
          }
        }

        // Motivo só faz sentido na edição (criação não gera log de versão).
        return this.confirmarMotivoOuProducao(row.view_name, {
          permiteMotivo: action === 'update',
          titulo: 'Salvar em produção?',
          texto: 'Você está alterando uma consulta em produção.',
          confirmar: 'Confirmar e salvar',
        });
      },
      /**
       * Fluxo compartilhado de motivo/confirmação (edição e exclusão):
       *  - ambiente exige motivo -> ModalMotivoAlteracao; resolve { motivo }
       *    para mesclar no corpo da requisição, ou false ao cancelar;
       *  - produção (sem exigir motivo) -> confirmação simples;
       *  - senão -> true (segue direto).
       */
      async confirmarMotivoOuProducao(viewName, { permiteMotivo = true, titulo, texto, confirmar } = {}) {
        if (permiteMotivo && this.exigeMotivo) {
          return this.$refs.motivoModal.open({
            ambiente: this.ambiente,
            viewName,
          });
        }

        if (this.isProducao) {
          const res = await this.$swal({
            title: titulo,
            text: texto,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: confirmar,
            cancelButtonText: 'Cancelar',
          });
          return !!(res && (res.isConfirmed || res.value));
        }

        return true;
      },
      loadViewsSincronizaveis() {
        get('/admin/sqls-erp/views-sincronizaveis')
          .then((response) => { this.viewsSincronizaveis = response || []; })
          .catch(() => { this.viewsSincronizaveis = []; });
      },
    },
    beforeMount() {
      this.termo = this.$route.query.q || '';
      this.loadData();
      this.loadViewsSincronizaveis();
    },
  }
</script>

<style scoped>

/* Banner de ambiente */
.ambiente-banner {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 0 14px;
  padding: 11px 16px;
  font-size: 14px;
  border-radius: 6px;
}

.ambiente-banner--prod {
  color: #8c2a20;
  background: #fdeaea;
  border-left: 4px solid #c0392b;
}

.ambiente-banner--prod svg {
  color: #c0392b;
}

.ambiente-banner--impl {
  color: #7a5b12;
  background: #fdf3e2;
  border-left: 4px solid #c1751f;
}

.ambiente-banner--impl svg {
  color: #c1751f;
}

/* Nota explicativa recolhível */
.nota {
  margin: 0 0 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  overflow: hidden;
}

.nota__head {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 11px 16px;
  font-size: 14px;
  font-weight: 700;
  color: #1a7a83;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.nota__chevron {
  transition: transform 0.18s ease;
  transform: rotate(-90deg);
}

.nota--aberta .nota__chevron {
  transform: rotate(0deg);
}

.nota__corpo {
  padding: 0 16px 13px 38px;
  font-size: 13.5px;
  line-height: 1.5;
  color: #1a7a83;
}

/* Barra de ferramentas */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.toolbar__search {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 1 1 280px;
  max-width: 460px;
}

.toolbar__search-icon {
  position: absolute;
  left: 12px;
  color: #949aa8;
  pointer-events: none;
}

.toolbar__search input {
  width: 100%;
  padding: 9px 12px 9px 34px;
  font-size: 14px;
  color: #2c2e38;
  background: #fff;
  border: 1px solid #d3d8e0;
  border-radius: 6px;
}

.toolbar__search input:focus {
  outline: none;
  border-color: #3a3e81;
}

.btn-novo {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background: #3a3e81;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
}

.btn-novo:hover {
  background: #2e3268;
}

/* Tabela */
.tabela-views thead th {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #949aa8;
  border-top: none;
}

.text-right {
  text-align: right;
}

.vazio {
  padding: 36px 12px;
  text-align: center;
  color: #949aa8;
}

.rodape-contagem {
  margin-top: 12px;
  font-size: 13px;
  color: #949aa8;
}
</style>
